import { useDispatchThunk } from "@common/redux/dispatchThunk";
import { useSelectSlice } from "@common/redux/selectors/useSelectSlice";
import {
  changeQueryFilters,
  setTags,
  IFilters,
  IMeta,
  setFilters
} from "@common/redux/slices/entities.slice";
import { localesSlice } from "@common/redux/slices/locales.slice";
import { IBaseTag, IGenre, ITag } from "@common/redux/types/tags";
import { CheckboxSelect, ICheckboxSelectOption } from "@kursk/components/ui/CheckboxSelect/CheckboxSelect";
import {
  CheckboxSelectSimple,
  ICheckboxSelectSimpleOption
} from "@kursk/components/ui/CheckboxSelectSimple/CheckboxSelectSimple";
import { DropdownButton } from "@kursk/components/ui/DropdownButton/DropdownButton";
import { Checkbox } from '@kursk/components/ui/Checkbox/Checkbox';
import { FilterTags } from "@kursk/components/ui/FilterTags/FilterTags";
import { useDispatch } from "react-redux";
import styles from './Filters.module.scss';
import { useOrientations } from "@common/hooks/useOrientation";
import React, { ReactElement, useEffect, useState, ChangeEvent } from "react";
import classNames from "classnames";
import { DateFilter } from "../DateFilter/DateFilter";
import { MobileFilters } from "@kursk/components/ui/Filters/MobileFilters/MobileFilters";
import { ArrowDownIcon } from "@kursk/components/ui/icons";
import { RangeFilter } from "@kursk/components/ui/Filters/RangeFilter/RangeFilter";
import { getPriceSlice } from "@common/redux/slices/minAndMaxPrices.slice";
import { EntityType } from "@common/redux/types/common";
import { eventsPlacesSlice } from "@common/redux/slices/eventsPlaces.slice";
import { isEmpty as _isEmpty, isString } from 'lodash';
import { push } from "connected-next-router";
import { getTypesUrl } from "@common/utils/getTypesUrl";
import { regionContent } from "@kursk/content/regionContent";

interface IProps {
  entityType: EntityType;
  meta: IMeta;
  filters: IFilters;
  withAudio?: boolean;
  withLocales?: boolean;
  withPlaces?: boolean;
  withPrices?: boolean;
  withDuration?: boolean;
  withDates?: boolean;
  withTransportation?: boolean;
  withPricesOptions?: {
    withFreeCheckbox?: boolean;
    withTicketsCheckbox?: boolean;
    dropDownTitle?: string;
  };
  withDurationOptions?: {
    durationsRange?: IRange;
    dropDownTitle?: string;
  };
  withTransportationOptions?: {
    items?: ICheckboxSelectSimpleOption[];
    filterField?: string;
  };
  withOrganizations?: boolean;
  withOrganizationsOptions?: {
    items?: ICheckboxSelectOption[];
    filterField?: string;
  };
  withSellerNames?: boolean;
  withSellerNamesOptions?: {
    items?: ICheckboxSelectOption[];
    filterField?: string;
    withSearch: boolean;
  };
  withSubjectNames?: boolean;
  withSubjectNamesOptions?: {
    items?: ICheckboxSelectOption[];
    filterField?: string;
    withSearch: boolean;
  };
}

interface IRange {
  min: number;
  max: number;
}

export function Filters({
  entityType,
  meta,
  filters,
  withLocales,
  withPlaces,
  withPrices,
  withAudio,
  withTransportation,
  withPricesOptions,
  withDates,
  withDuration,
  withDurationOptions,
  withTransportationOptions,
  withOrganizations,
  withOrganizationsOptions,
  withSellerNames,
  withSellerNamesOptions,
  withSubjectNames,
  withSubjectNamesOptions
}: IProps): ReactElement {
  const dispatch = useDispatch();
  const dispatchThunk = useDispatchThunk();
  const { items: locales } = useSelectSlice(localesSlice);
  const { items: allPlaces } = useSelectSlice(eventsPlacesSlice);
  const pricesRange = useSelectSlice(getPriceSlice)[entityType];
  const orientation = useOrientations();
  const [isOpenMobileFilters, setOpenMobileFilters] = useState(false);
  const [isFree, setIsFree] = useState(filters?.isFree || false);
  useEffect(() => {
    setIsFree(filters?.isFree);
  }, [filters?.isFree]);
  const [withTickets, setWithTickets] = useState(filters?.withTickets || false);
  useEffect(() => {
    setWithTickets(filters?.withTickets);
  }, [filters?.withTickets]);

  const disabledLocales = meta?.locales?.length ?
    locales.filter((item) => !meta?.locales?.find((i) => i.name === item.name)) : [];
  // if in filters already exist maxPrice then init IRange, else undefined
  const [selectedPrice, setSelectedPrice] = useState<IRange | undefined>(filters?.maxPrice ?
    {
      min: filters?.minPrice || 0,
      max: filters?.maxPrice
    } : undefined);
  const [selectedDuration, setSelectedDuration] = useState<IRange | undefined>(filters?.durationTo ?
    {
      min: filters?.durationFrom || 0,
      max: filters?.durationTo || 0
    } : undefined);

  useEffect(() => {
    if (filters?.maxPrice) {
      setSelectedPrice({
        min: filters?.minPrice || 0,
        max: filters?.maxPrice
      })
    }
  }, [filters?.maxPrice, filters?.minPrice]);

  const onTagClick = async (tag: ITag) => {
    dispatch(setTags(entityType, tag));
    await dispatchThunk(changeQueryFilters(entityType));
  }

  const onGenreClick = async (genre: IGenre) => {
    dispatch(setTags(entityType, genre));
    await dispatchThunk(changeQueryFilters(entityType));
  }

  const onTypesClick = async (type: IBaseTag) => {

    let newAllTypes = [];

    if (filters.type?.includes(type._id)) {
      newAllTypes = filters.type?.filter((value) => value !== type._id);
    } else {
      newAllTypes = [...filters.type, type._id];
    }

    const { newPathname, newQuery } = getTypesUrl(newAllTypes, meta.types);

    dispatch(push({
      pathname: newPathname,
      query: newQuery,
    }));
  }

  const onLocalesChange = async (values: number[]) => {
    dispatch(setFilters(entityType, { locales: values }));
    await dispatchThunk(changeQueryFilters(entityType));
  }

  const onOrganizationsChange = async (values: number[]) => {
    dispatch(setFilters(entityType, { [withOrganizationsOptions.filterField]: values }));
    await dispatchThunk(changeQueryFilters(entityType));
  }

  const onSellerNamesChange = async (values: string[]) => {
    dispatch(setFilters(entityType, { [withSellerNamesOptions.filterField]: values }));
    await dispatchThunk(changeQueryFilters(entityType));
  }

  const onSubjectNamesChange = async (values: string[]) => {
    dispatch(setFilters(entityType, { [withSubjectNamesOptions.filterField]: values }));
    await dispatchThunk(changeQueryFilters(entityType));
  }

  const onPlacesChange = async (values: number[]) => {
    dispatch(setFilters(entityType, { places: values }));
    await dispatchThunk(changeQueryFilters(entityType));
  }

  const onTransportationsChange = async (values: string[]) => {
    dispatch(setFilters(entityType, { [withTransportationOptions.filterField]: values }));
    await dispatchThunk(changeQueryFilters(entityType));
  }

  const onHasAudioChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.checked;
    dispatch(setFilters(entityType, { hasAudio: value }));
    await dispatchThunk(changeQueryFilters(entityType));
  }

  const onDateChange = async ({ start, end }) => {
    dispatch(setFilters(entityType, { start, end }));
    await dispatchThunk(changeQueryFilters(entityType));
  }

  const getLocalesFilterTitle = () => {
    const localesCount = filters?.locales?.length;
    return `Город или район${localesCount ? ` (${localesCount})` : ''}`;
  }

  const getSourcePrice = (): string => {
    switch (entityType) {
      case 'places':
        return 'place'
      case 'hotels':
        return 'hotel'
      case 'restaurants':
        return 'restaurant'
      default:
        return ''
    }
  }

  const getPlacesFilterTitle = () => {
    const placesCount = filters?.places?.length;
    return `Место проведения${placesCount ? ` (${placesCount})` : ''}`;
  }

  const getTransportationFilterTitle = () => {
    const count = filters?.[withTransportationOptions.filterField]?.length;
    const title = regionContent.filters?.touristroutes?.transportationMethodTitle ?? 'Способ передвижения';

    return `${title}${count ? ` (${count})` : ''}`;
  }

  const getOrganizationsFilterTitle = () => {
    const count = filters?.[withOrganizationsOptions.filterField]?.length;
    return `Гид${count ? ` (${count})` : ''}`;
  }

  const getSellerNamesFilterTitle = () => {
    const filterValues = filters?.[withSellerNamesOptions.filterField];
    const count = isString(filterValues) ? filterValues.split(',').length : filterValues.length;
    return `Продавец${count ? ` (${count})` : ''}`;
  }

  const getSubjectNamesFilterTitle = () => {
    const filterValues = filters?.[withSubjectNamesOptions.filterField];
    const count = isString(filterValues) ? filterValues.split(',').length : filterValues.length;
    return `Категория товара${count ? ` (${count})` : ''}`;
  }

  const getPriceString = (filters: IFilters) => {
    if (!filters) {
      return null;
    }
    let pricesCount = 0;
    if (filters.isFree) {
      pricesCount++;
    }
    if (filters.withTickets) {
      pricesCount++;
    }
    if (filters?.minPrice > 0 || filters?.maxPrice > 0) {
      pricesCount++;
    }
    return `Цена${pricesCount ? ` (${pricesCount})` : ''}`;
  }

  const updatePriceFilters = async (minPrice?: number, maxPrice?: number): Promise<void> => {
    const params: Record<string, number | string | boolean> = {};
    if (maxPrice) {
      params.maxPrice = maxPrice;
      params.minPrice = minPrice;
      if (getSourcePrice()) {
        params.priceSource = getSourcePrice();
      }
    } else {
      params.maxPrice = null;
      params.minPrice = null;
      params.priceSource = null;
    }
    params.isFree = isFree;
    params.withTickets = withTickets;
    if (!_isEmpty(params)) {
      dispatch(setFilters(entityType, params));
    }
    await dispatchThunk(changeQueryFilters(entityType));
  }
  const updateMobileFilter = async (
    locales: boolean, places: boolean, audio: boolean, transportations: boolean, organizations: boolean,
    sellerNames: boolean, subjectNames: boolean,
    selectedLocales: number[], selectedPlaces: number[], hasAudio: boolean, selectedTransportations: string[],
    selectedOrganizations: string[], selectedSellerNames: string[], selectedSubjectNames: string[]
  ) => {
    dispatch(setFilters(entityType, {
      ...locales && { locales: selectedLocales },
      ...places && { places: selectedPlaces },
      ...audio && { hasAudio: hasAudio },
      ...transportations && { [withTransportationOptions.filterField]: selectedTransportations },
      ...organizations && { [withOrganizationsOptions.filterField]: selectedOrganizations },
      ...sellerNames && { [withSellerNamesOptions.filterField]: selectedSellerNames },
      ...subjectNames && { [withSubjectNamesOptions.filterField]: selectedSubjectNames },
      ...getSourcePrice() && { priceSource: selectedPrice ? getSourcePrice() : null },
      maxPrice: selectedPrice?.max,
      minPrice: selectedPrice?.min,
      durationTo: selectedDuration?.max,
      durationFrom: selectedDuration?.min,
      isFree,
      withTickets,
    }));

    await dispatchThunk(changeQueryFilters(entityType));
  }
  const updateStreams = async (places: boolean, selectedPlaces: number[], streamsRadioValue: string) => {
    dispatch(setFilters(entityType, {
      ...places && { places: selectedPlaces },
      isLive: streamsRadioValue === 'liveStreams',
      isPreview: streamsRadioValue === 'previewStreams',
      isArchive: streamsRadioValue === 'archiveStreams',
    }));
    await dispatchThunk(changeQueryFilters(entityType));
  }

  const getDurationString = (filters: IFilters) => {
    if (!filters) {
      return null;
    }
    let durationsCount = 0;

    if (filters?.durationFrom > 0 || filters?.durationTo > 0) {
      durationsCount++;
    }
    return `Длительность${durationsCount ? ` (${durationsCount})` : ''}`;
  }

  const updateDurationFilters = async (durationFrom?: number, durationTo?: number): Promise<void> => {
    const params: Record<string, number | string | boolean> = {};

    if (durationTo) {
      params.durationTo = durationTo;
      params.durationFrom = durationFrom;
    } else {
      params.durationTo = null;
      params.durationFrom = null;
    }
    if (!_isEmpty(params)) {
      dispatch(setFilters(entityType, params));
    }
    await dispatchThunk(changeQueryFilters(entityType));
  }
  const showMobileFilters = withPrices || withDuration || withTransportation;
  return (
    <>
      {orientation !== 'mobile' && entityType !== "mapPoints" && (
        <div className={styles.filtersRow}>
          <div className={styles.filterBlock}>
            {withSellerNames && (
              <CheckboxSelect
                className={styles.filter}
                title={getSellerNamesFilterTitle()}
                items={withSellerNamesOptions.items}
                selectedItems={filters[withSellerNamesOptions.filterField]}
                onChange={onSellerNamesChange}
                placeholder="Введите название продавца"
                disabledArray={[]}
                withSearch={withSellerNamesOptions.withSearch}
              />
            )}
            {withSubjectNames && (
              <CheckboxSelect
                className={styles.filter}
                title={getSubjectNamesFilterTitle()}
                items={withSubjectNamesOptions.items}
                selectedItems={filters[withSubjectNamesOptions.filterField]}
                onChange={onSubjectNamesChange}
                placeholder="Введите категорию"
                disabledArray={[]}
                withSearch={withSubjectNamesOptions.withSearch}
              />
            )}
            {withLocales && (
              <CheckboxSelect
                className={styles.filter}
                title={getLocalesFilterTitle()}
                items={locales}
                disabledArray={(withPlaces && filters.places?.length) ? disabledLocales : []}
                selectedItems={filters.locales}
                onChange={onLocalesChange}
                placeholder="Введите название района"
              />
            )}
            {withPlaces && (
              <CheckboxSelect
                className={styles.filter}
                title={getPlacesFilterTitle()}
                items={allPlaces}
                disabledArray={[]}
                selectedItems={filters.places}
                onChange={onPlacesChange}
                placeholder="Введите название места"
              />
            )}
            {withTransportation && (
              <CheckboxSelectSimple
                className={styles.filter}
                title={getTransportationFilterTitle()}
                items={withTransportationOptions.items}
                selectedItems={filters[withTransportationOptions.filterField]}
                onChange={onTransportationsChange}
              />
            )}
            {withOrganizations && (
              <CheckboxSelect
                className={styles.filter}
                title={getOrganizationsFilterTitle()}
                items={withOrganizationsOptions.items}
                selectedItems={filters[withOrganizationsOptions.filterField]}
                onChange={onOrganizationsChange}
                placeholder="Введите название гида"
                disabledArray={[]}
              />
            )}
            {withAudio && (
              <div className={styles.typeCheckbox}>
                <Checkbox value="hasAudio" checked={filters.hasAudio} onChange={onHasAudioChange}>Аудиогид</Checkbox>
              </div>
            )}
          </div>
          <div className={styles.filterBlock}>
            {withDuration && withDurationOptions && withDurationOptions.durationsRange && (
              <>
                <DropdownButton
                  className={styles.filterButton}
                  contentClassName={styles.filterButtonContent}
                  renderButton={({ isOpen, toggle }) => (
                    <button
                      className={classNames(styles.freeButton, isOpen && styles.freeButtonSelected)}
                      onClick={() => {
                        toggle();
                      }}
                    >
                      <span>{getDurationString(filters)}</span>
                      <ArrowDownIcon width={12} height={5}/>
                    </button>
                  )}
                  renderContent={({ toggle }) => (
                    <RangeFilter
                      rangeValues={withDurationOptions.durationsRange}
                      setIsShowFilter={toggle}
                      updateRangeFilters={updateDurationFilters}
                      selectedRangeValue={selectedDuration}
                      setSelectedRangeValue={setSelectedDuration}
                      dropDownTitle={withDurationOptions.dropDownTitle}
                    />
                  )}
                />
              </>
            )}

            {pricesRange &&
              <>
                <DropdownButton
                  className={styles.filterButton}
                  contentClassName={styles.filterButtonContent}
                  renderButton={({ isOpen, toggle }) => (
                    <button
                      className={classNames(styles.freeButton, isOpen && styles.freeButtonSelected)}
                      onClick={() => {
                        toggle();
                      }}
                    >
                      <span>{getPriceString(filters)}</span>
                      <ArrowDownIcon width={12} height={5}/>
                    </button>
                  )}
                  renderContent={({ toggle }) => (
                    <RangeFilter
                      selectedRangeValue={selectedPrice}
                      setSelectedRangeValue={setSelectedPrice}
                      setIsFree={setIsFree}
                      isFree={isFree}
                      setWithTickets={setWithTickets}
                      withTickets={withTickets}
                      setIsShowFilter={toggle}
                      rangeValues={{ min: pricesRange?.min, max: pricesRange?.max }}
                      updateRangeFilters={updatePriceFilters}
                      withFreeCheckbox={withPricesOptions?.withFreeCheckbox}
                      withTicketsCheckbox={withPricesOptions?.withTicketsCheckbox}
                      dropDownTitle={withPricesOptions?.dropDownTitle}
                    />
                  )}
                />
              </>
            }
          </div>
        </div>
      )}
      {(orientation === 'mobile' && showMobileFilters) && (
        <MobileFilters
          filters={filters}
          disabledLocales={(withPlaces && filters.places?.length) ? disabledLocales : []}
          disabledPlaces={[]}
          close={() => setOpenMobileFilters(false)}
          open={() => setOpenMobileFilters(true)}
          isOpen={isOpenMobileFilters}
          allLocales={locales}
          updateMobileFilter={updateMobileFilter}
          updateStreams={updateStreams}
          allPlaces={allPlaces}
          entityType={entityType}
          withLocales={withLocales}
          withPlaces={withPlaces}
          withPrices={withPrices}
          withPricesOptions={{
            ...withPricesOptions,
            pricesRange,
            selectedRangeValue: selectedPrice,
            setSelectedRangeValue: setSelectedPrice,
            withTickets,
            setWithTickets,
            isFree,
            setIsFree,
          }}
          withAudio={withAudio}
          withDuration={withDuration}
          withDurationOptions={{
            ...withDurationOptions,
            selectedRangeValue: selectedDuration,
            setSelectedRangeValue: setSelectedDuration,
          }}
          withTransportation={withTransportation}
          withTransportationOptions={withTransportationOptions}
          withOrganizations={withOrganizations}
          withOrganizationsOptions={withOrganizationsOptions}
          withSellerNames={withSellerNames}
          withSellerNamesOptions={withSellerNamesOptions}
          withSubjectNames={withSubjectNames}
          withSubjectNamesOptions={withSubjectNamesOptions}
        />
      )}
      {withDates && (
        <DateFilter
          leftDatesDefault={0}
          rightDatesDefault={90}
          onChange={onDateChange}
          filters={filters}
          disablePastDatesOnCalendar
        />
      )}
      {!!meta?.tags?.length && (
        <FilterTags
          selected={filters?.allTags}
          tags={meta?.tags}
          onTagClick={onTagClick}
        />
      )}
      {!!meta?.genres?.length && (
        <FilterTags
          selected={filters?.genres}
          tags={meta?.genres}
          onTagClick={onGenreClick}
        />
      )}
      {!!meta?.types?.length && (
        <FilterTags
          selected={filters?.type}
          tags={meta?.types}
          onTagClick={onTypesClick}
          bordered={false}
        />
      )}
    </>
  );
}
